import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MentalHealthState } from './MentalHealthTypes';
import {
  getMentalHealthResultsOperation,
  getMentalHealthStatsOperation,
  getMentalHealthTestOperation,
  submitMentalHealthResultOperation,
} from './mentalHealth-operations';

const initialState: MentalHealthState = {
  test: null,
  lastResult: null,
  history: [],
  stats: null,
  submittedResult: null,
  answers: {},
  showForm: false,
  currentQuestionIndex: 0,
  testLoading: false,
  resultsLoading: false,
  statsLoading: false,
  submitting: false,
  testError: null,
  resultsError: null,
  statsError: null,
  submitError: null,
};

const clearDraftProgress = (state: MentalHealthState) => {
  state.answers = {};
  state.showForm = false;
  state.currentQuestionIndex = 0;
};

const mentalHealthSlice = createSlice({
  name: 'mentalHealth',
  initialState,
  reducers: {
    resetMentalHealthState: () => initialState,
    clearMentalHealthError: state => {
      state.testError = null;
      state.resultsError = null;
      state.statsError = null;
      state.submitError = null;
    },
    setMentalHealthAnswer: (
      state,
      { payload }: PayloadAction<{ questionId: number; score: number }>,
    ) => {
      state.answers[payload.questionId] = payload.score;
    },
    clearMentalHealthAnswers: state => {
      state.answers = {};
    },
    setMentalHealthShowForm: (state, { payload }: PayloadAction<boolean>) => {
      state.showForm = payload;
    },
    setMentalHealthCurrentQuestionIndex: (state, { payload }: PayloadAction<number>) => {
      state.currentQuestionIndex = payload;
    },
    startMentalHealthTest: state => {
      state.answers = {};
      state.showForm = true;
      state.currentQuestionIndex = 0;
      state.testError = null;
      state.resultsError = null;
      state.statsError = null;
      state.submitError = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(getMentalHealthTestOperation.pending, state => {
      state.testLoading = true;
      state.testError = null;
    });
    builder.addCase(getMentalHealthTestOperation.fulfilled, (state, { payload }) => {
      state.test = payload.test;
      state.lastResult = payload.last_result;
      state.testLoading = false;
    });
    builder.addCase(getMentalHealthTestOperation.rejected, (state, { payload, error }) => {
      state.test = null;
      state.lastResult = null;
      state.testLoading = false;
      state.testError = payload?.message || error.message || null;
    });

    builder.addCase(getMentalHealthResultsOperation.pending, state => {
      state.resultsLoading = true;
      state.resultsError = null;
    });
    builder.addCase(getMentalHealthResultsOperation.fulfilled, (state, { payload }) => {
      state.history = payload;
      state.resultsLoading = false;
    });
    builder.addCase(getMentalHealthResultsOperation.rejected, (state, { payload, error }) => {
      state.history = [];
      state.resultsLoading = false;
      state.resultsError = payload?.message || error.message || null;
    });

    builder.addCase(getMentalHealthStatsOperation.pending, state => {
      state.statsLoading = true;
      state.statsError = null;
    });
    builder.addCase(getMentalHealthStatsOperation.fulfilled, (state, { payload }) => {
      state.stats = payload;
      state.statsLoading = false;
    });
    builder.addCase(getMentalHealthStatsOperation.rejected, (state, { payload, error }) => {
      state.stats = null;
      state.statsLoading = false;
      state.statsError = payload?.message || error.message || null;
    });

    builder.addCase(submitMentalHealthResultOperation.pending, state => {
      state.submitting = true;
      state.submitError = null;
    });
    builder.addCase(submitMentalHealthResultOperation.fulfilled, (state, { payload }) => {
      state.submittedResult = payload;
      state.submitting = false;
      clearDraftProgress(state);
    });
    builder.addCase(submitMentalHealthResultOperation.rejected, (state, { payload, error }) => {
      state.submitting = false;
      state.submitError = payload?.message || error.message || null;
    });
  },
});

export const {
  resetMentalHealthState,
  clearMentalHealthError,
  setMentalHealthAnswer,
  clearMentalHealthAnswers,
  setMentalHealthShowForm,
  setMentalHealthCurrentQuestionIndex,
  startMentalHealthTest,
} = mentalHealthSlice.actions;
export default mentalHealthSlice.reducer;
